import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Input, Empty, Dropdown, DatePicker, Select } from "antd";
import { SearchOutlined, MoreOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import moment from "moment";
import { getInactiveCoursesList } from "../../appRedux/actions/courses";
import { getPlatformsList } from "../../appRedux/actions/platforms";
import Page from "../../components/Common/Page";
import PagesHeader from "../../components/Common/PagesHeader";
import ArchiveModal from "../../components/Courses/ArchiveModal";

const { RangePicker } = DatePicker;

const CourseArchive = () => {
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);

  const {
    inactiveCourseList,
    inactiveTotalRecords,
    inactiveQueryInfo,
    loader,
    platformList,
  } = useSelector((state) => ({
    inactiveCourseList: state.courses.inactiveCourseList || [],
    inactiveTotalRecords: state.courses.inactiveTotalRecords || 0,
    inactiveQueryInfo: state.courses.inactiveQueryInfo || { page: 1, size: 5 },
    loader: state.common.loader,
    platformList: state.platforms.platformList || [],
  }));

  const fetchArchiveWithFilters = (
    page,
    size,
    searchVal = searchText,
    dates = dateRange,
    platforms = selectedPlatforms
  ) => {
    const query = {
      page,
      size,
    };
    if (searchVal) {
      query.title = searchVal;
    }
    if (dates && dates[0] && dates[1]) {
      query.startDate = dates[0].format("YYYY-MM-DD");
      query.endDate = dates[1].format("YYYY-MM-DD");
    }
    if (platforms && platforms.length > 0) {
      query.platform = platforms.join(",");
    }
    dispatch(getInactiveCoursesList(query));
  };

  useEffect(() => {
    dispatch(getPlatformsList({ page: 1, size: 100 }));
    fetchArchiveWithFilters(1, 5);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (val) => {
    setSearchText(val);
    fetchArchiveWithFilters(1, inactiveQueryInfo.size || 5, val, dateRange, selectedPlatforms);
  };

  const handleDateChange = (dates) => {
    setDateRange(dates);
    fetchArchiveWithFilters(1, inactiveQueryInfo.size || 5, searchText, dates, selectedPlatforms);
  };

  const handlePlatformChange = (value) => {
    setSelectedPlatforms(value);
    fetchArchiveWithFilters(1, inactiveQueryInfo.size || 5, searchText, dateRange, value);
  };

  const handleTableChange = (pagination) => {
    const { current, pageSize } = pagination;
    fetchArchiveWithFilters(current, pageSize, searchText, dateRange, selectedPlatforms);
  };

  const columns = [
    {
      key: "title",
      title: "Title",
      dataIndex: "title",
      render: (title, record) => (
        <h2 className="table-avatar" style={{ whiteSpace: "nowrap" }}>
          <Link to={`/course/${record._id}`}>{title}</Link>
        </h2>
      ),
    },
    {
      key: "merchant",
      title: "Platform",
      dataIndex: "merchant",
      align: "center",
      render: (merchant) => (
        <span className="badge bg-inverse-warning text-capitalize" style={{ padding: "6px 12px", borderRadius: "4px" }}>
          {merchant}
        </span>
      ),
    },
    {
      key: "category",
      title: "Category",
      dataIndex: "category",
      align: "center",
      render: (category) => (
        <p className="px-3 py-2 m-0 text-capitalize">
          {category && category.name ? category.name : ""}
        </p>
      ),
    },
    {
      key: "archivedAt",
      title: "Archived At",
      dataIndex: "updated_at",
      align: "center",
      render: (updatedAt) => (
        <p className="px-3 py-2 m-0">
          {updatedAt ? moment(updatedAt).format("DD MMM YYYY, hh:mm A") : "N/A"}
        </p>
      ),
    },
    {
      key: "action",
      title: "Action",
      align: "center",
      render: (_, record) => (
        <div className="text-center">
          <Dropdown
            menu={{
              items: [
                {
                  key: "activate",
                  label: <ArchiveModal info={record} isArchive={true} />,
                },
              ]
            }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <button
              style={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                padding: "6px",
                borderRadius: "6px",
                transition: "background 0.2s"
              }}
              className="action-menu-btn"
            >
              <MoreOutlined style={{ fontSize: "18px", color: "#6b7280" }} />
            </button>
          </Dropdown>
        </div>
      ),
    },
  ];

  const itemRender = (_, type, originalElement) => {
    if (type === "prev") return <a>Previous</a>;
    if (type === "next") return <a>Next</a>;
    return originalElement;
  };

  return (
    <Page title="Archive">
      <div className="page-wrapper">
        <div className="content container-fluid">
          <PagesHeader
            pageTitle="Archive"
            homePageUrl="/"
            homePageText="Dashboard"
            innerPageUrl="/course/list"
            innerPageText="Courses"
            activePageText="Archive"
            floatRight={
              <button
                className="btn"
                style={{
                  backgroundColor: "#e0533c",
                  color: "#ffffff",
                  borderColor: "#e0533c",
                  borderRadius: "6px",
                  height: "40px",
                  padding: "0 18px",
                  fontSize: "14px",
                  fontWeight: "500",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px"
                }}
                onClick={() => {}}
              >
                <i className="fa fa-trash" /> Clear Archive
              </button>
            }
          />

          <div className="row filter-row mb-4 align-items-center" style={{ gap: "16px", paddingLeft: "15px" }}>
            <div style={{ width: "300px" }}>
              <Input
                placeholder="Search archived courses by title..."
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                style={{
                  height: "40px",
                  borderRadius: "6px",
                  border: "1px solid #d9d9d9",
                }}
              />
            </div>
            <div style={{ width: "240px" }}>
              <Select
                mode="multiple"
                allowClear
                size="large"
                placeholder="Filter by platform"
                style={{ width: "100%" }}
                value={selectedPlatforms}
                onChange={handlePlatformChange}
                maxTagCount="responsive"
                options={platformList.map((p) => ({
                  label: p.name || p.merchant,
                  value: p.merchant || p.name,
                }))}
              />
            </div>
            <div style={{ width: "320px" }}>
              <RangePicker
                size="large"
                value={dateRange}
                onChange={handleDateChange}
                style={{
                  width: "100%",
                  borderRadius: "6px",
                  border: "1px solid #d9d9d9",
                }}
              />
            </div>
          </div>

          <div className="card shadow-sm border-0" style={{ borderRadius: "8px" }}>
            <div className="card-body p-0">
              <div className="table-responsive">
                <Table
                  className="modern-course-table"
                  loading={loader}
                  scroll={{ x: "max-content" }}
                  locale={{
                    emptyText: (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                          <span style={{ color: "#6b7280", fontSize: "14px" }}>
                            No archived courses found.
                          </span>
                        }
                      />
                    ),
                  }}
                  pagination={{
                    total: inactiveTotalRecords,
                    current: inactiveQueryInfo.page || 1,
                    pageSize: inactiveQueryInfo.size || 5,
                    showSizeChanger: true,
                    showTotal: (total, range) =>
                      `Showing ${range[0]} to ${range[1]} of ${total} entries`,
                    itemRender: itemRender,
                  }}
                  onChange={handleTableChange}
                  columns={columns}
                  dataSource={inactiveCourseList}
                  rowKey={(record) => record._id}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
};

export default CourseArchive;
